import { useState } from "react";
import { GrammarPointList } from "./GrammarPointList";
import type { BookLesson, BookPageLine, BookRow } from "./bookLesson.types";

/** Each section owns a hue so the reader can tell grammar from vocabulary at a glance. */
const TONES = {
  primary: {
    rail: "bg-primary-500",
    head: "bg-primary-500/10",
    text: "text-primary-700 dark:text-primary-300",
  },
  accent: {
    rail: "bg-accent-500",
    head: "bg-accent-500/10",
    text: "text-accent-700 dark:text-accent-400",
  },
  warning: {
    rail: "bg-warning-500",
    head: "bg-warning-500/10",
    text: "text-warning-700 dark:text-warning-500",
  },
  secondary: {
    rail: "bg-secondary-500",
    head: "bg-secondary-500/10",
    text: "text-secondary-700 dark:text-secondary-300",
  },
  neutral: {
    rail: "bg-gray-300 dark:bg-gray-600",
    head: "",
    text: "text-gray-500 dark:text-gray-400",
  },
} as const;

type Tone = keyof typeof TONES;

function Section({
  title,
  tone,
  count,
  children,
}: {
  title: string;
  tone: Tone;
  count?: number;
  children: React.ReactNode;
}) {
  const { rail, head, text } = TONES[tone];
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
      <header
        className={`flex items-center gap-2.5 border-b border-gray-200 dark:border-gray-800 px-4 py-2.5 ${head}`}
      >
        <span className={`h-3.5 w-1 rounded-full ${rail}`} />
        <h3 className={`text-[11px] font-extrabold uppercase tracking-widest ${text}`}>{title}</h3>
        {count !== undefined && (
          <span className={`text-[11px] font-bold tabular-nums opacity-70 ${text}`}>{count}</span>
        )}
      </header>
      <div className="px-4 py-3.5">{children}</div>
    </section>
  );
}

function Disclosure({
  open,
  onToggle,
  children,
}: { open: boolean; onToggle: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="text-xs font-bold text-primary-600 dark:text-primary-300 hover:underline underline-offset-2"
      aria-expanded={open}
    >
      {children}
    </button>
  );
}

/**
 * What the lesson as a whole is doing. Absent renders nothing — most lessons genuinely have no
 * context, and a standing empty box teaches the reader to stop looking at it.
 */
function LessonContext({ text }: { text: string | null }) {
  const [open, setOpen] = useState(false);
  const paragraphs = (text ?? "")
    .split(/\n{2,}/u)
    .map((one) => one.trim())
    .filter((one) => one !== "");

  if (paragraphs.length === 0) return null;
  const [lead, ...rest] = paragraphs;

  return (
    <Section title="Context" tone="primary">
      <div className="space-y-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
        <p>{lead}</p>
        {open && rest.map((paragraph) => <p key={paragraph.slice(0, 40)}>{paragraph}</p>)}
        {rest.length > 0 && !open && (
          <Disclosure open={open} onToggle={() => setOpen(true)}>
            Show full context
          </Disclosure>
        )}
      </div>
    </Section>
  );
}

function GrammarPoints({ points }: { points: BookRow[] }) {
  if (points.length === 0) return null;
  return (
    <Section title="Grammar points" tone="accent" count={points.length}>
      <GrammarPointList points={points} />
    </Section>
  );
}

/** Kept apart from the points: presenting an illustration as a rule is how a generator comes to
 *  teach the example. */
function GrammarExamples({ examples }: { examples: BookRow[] }) {
  if (examples.length === 0) return null;
  return (
    <Section title="Grammar examples" tone="warning" count={examples.length}>
      <ul className="space-y-1.5">
        {examples.map((one) => (
          <li key={one.position} className="flex gap-2.5 text-sm">
            <span aria-hidden className="select-none text-warning-500">
              ›
            </span>
            <span>{one.text}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Words({ words, title = "Words" }: { words: BookRow[]; title?: string }) {
  if (words.length === 0) return null;
  return (
    <Section title={title} tone="secondary" count={words.length}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800">
            <th className="py-1.5 pr-4 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400">
              Word
            </th>
            <th className="py-1.5 pr-4 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400">
              Part of speech
            </th>
            <th className="py-1.5 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400">
              Example
            </th>
          </tr>
        </thead>
        <tbody>
          {words.map((word) => (
            // Two words of one lesson may read the same — `train (v)` and `train (n)` — so
            // position is part of the identity here.
            <tr
              key={word.position}
              className="border-b border-gray-100 dark:border-gray-800/70 last:border-0 align-top"
            >
              <th
                scope="row"
                className="py-2 pr-4 text-left font-semibold text-secondary-700 dark:text-secondary-300"
              >
                {word.text}
              </th>
              <td className="py-2 pr-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                {word.partOfSpeech ?? "—"}
              </td>
              <td className="py-2 text-gray-700 dark:text-gray-300">{word.example ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

/** Shown rather than dropped: `type` is a free string upstream and a new kind must not vanish. */
function UnknownKinds({ rows }: { rows: BookRow[] }) {
  if (rows.length === 0) return null;
  return (
    <Section title="Other rows" tone="neutral" count={rows.length}>
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <li key={`${row.type}:${row.position}`} className="flex gap-2.5 text-sm">
            <span className="shrink-0 font-mono text-xs text-gray-400">{row.type}</span>
            <span>{row.text}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Runs are consecutive only: a section name can reappear later in a lesson, and reading order rules. */
function toSectionRuns(lines: BookPageLine[]) {
  const runs: { section: string | null; lines: BookPageLine[] }[] = [];
  for (const line of lines) {
    const current = runs.at(-1);
    if (current && current.section === line.section) current.lines.push(line);
    else runs.push({ section: line.section, lines: [line] });
  }
  return runs;
}

function PageLines({ lesson }: { lesson: BookLesson }) {
  const [open, setOpen] = useState(false);

  if (lesson.pageLines.length === 0) {
    return (
      <Section title="Page lines" tone="neutral">
        <p className="text-xs text-gray-400 italic">No page lines were read for this lesson.</p>
      </Section>
    );
  }
  const truncated = lesson.pageLineCount > lesson.pageLines.length;

  return (
    <Section title="Page lines" tone="neutral" count={lesson.pageLines.length}>
      <div className="space-y-2.5">
        <div className="flex items-center gap-3">
          <Disclosure open={open} onToggle={() => setOpen(!open)}>
            {open ? "Hide page lines" : "Show page lines"}
          </Disclosure>
          {truncated && (
            <span className="text-xs text-gray-500">of {lesson.pageLineCount} read</span>
          )}
        </div>
        {open && (
          <div className="space-y-3">
            {toSectionRuns(lesson.pageLines).map((run, runIndex) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: positional run of lines
                key={`run-${runIndex}`}
                className="border-t border-gray-100 dark:border-gray-800/70 pt-2"
              >
                {run.section && (
                  <header className="flex items-center gap-2 pb-0.5">
                    <h4 className="text-[11px] font-extrabold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      {run.section}
                    </h4>
                    <span className="text-[11px] font-bold tabular-nums text-gray-400">
                      {run.lines.length}
                    </span>
                  </header>
                )}
                {/* A printed line has no identity but its place in reading order, and duplicates are
                    real — a book may print one sentence twice. */}
                {run.lines.map((line, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional line content
                  <p key={`line-${index}`} className="flex gap-3 py-1 text-xs">
                    <span className="w-24 shrink-0 font-mono text-gray-400">{line.kind}</span>
                    <span>{line.text}</span>
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

const KNOWN_ROW_TYPES = ["grammar", "grammarExamples", "word"];

function Chip({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  const { head, text } = TONES[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${head} ${text}`}
    >
      {children}
    </span>
  );
}

export function LessonDetail({ lesson }: { lesson: BookLesson }) {
  const of = (type: string) => lesson.rows.filter((row) => row.type === type);
  // One value per lesson, stored on each row that has it. Any of them is the lesson's context.
  const context = lesson.rows.find((row) => row.lessonContext)?.lessonContext ?? null;

  const grammar = of("grammar");
  const examples = of("grammarExamples");
  const words = of("word");
  // The book lists some words under a part of their own rather than with the main vocabulary, and
  // the generator scopes them that way too, so they are shown as the separate lists they are.
  const subLists = [...new Set(words.flatMap((row) => (row.subList ? [row.subList] : [])))];

  return (
    <div>
      <header className="sticky top-0 z-10 bg-background border-b border-gray-200 dark:border-gray-800 pb-3.5">
        <h2 className="card-title">{lesson.printedLabel}</h2>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Chip tone="primary">{lesson.levelName}</Chip>
          <Chip tone="primary">{lesson.componentName}</Chip>
          {grammar.length > 0 && <Chip tone="accent">{grammar.length} grammar</Chip>}
          {words.length > 0 && <Chip tone="secondary">{words.length} words</Chip>}
        </div>
      </header>
      <div className="space-y-5 pt-5">
        {/* Keyed on the lesson so the two disclosures below close when the selection changes. */}
        <LessonContext key={`context-${lesson.id}`} text={context} />
        <GrammarPoints points={grammar} />
        <GrammarExamples examples={examples} />
        <Words words={words.filter((row) => !row.subList)} />
        {subLists.map((name) => (
          <Words
            key={name}
            title={`Words · ${name}`}
            words={words.filter((row) => row.subList === name)}
          />
        ))}
        <UnknownKinds rows={lesson.rows.filter((row) => !KNOWN_ROW_TYPES.includes(row.type))} />
        <PageLines key={`lines-${lesson.id}`} lesson={lesson} />
      </div>
    </div>
  );
}
